import { useState, useEffect } from "react";
import useTranslation from "../useTranslation";
import { useCompanies } from "../../context/CompanyContext";
import {
  getAllProducts,
  getAllProductTypes,
  addNewProduct,
  associateBaseWithProduct,
  updateProduct,
  deleteProduct,
} from "../../api/productApi";
import { toast } from "react-toastify";

export const useProductManagement = () => {
  const { t } = useTranslation("productManagerPage");
  const { companies, loading } = useCompanies();

  // State for products
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [productTypeFilter, setProductTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Functions for products
  // Function to fetch products based on selected company
  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const data = await getAllProducts(selectedCompany);
      setProducts(data);
      setFilteredProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Function to fetch product types
  const fetchProductTypes = async () => {
    try {
      const data = await getAllProductTypes();
      setProductTypes(data);
    } catch (error) {
      console.error("Error fetching product types:", error);
      setProductTypes([]);
    }
  };

  // Function to apply filters to products
  const applyFilters = () => {
    try {
      setLoadingProducts(true);

      // Always filter from the original dataset
      const newFilteredProducts = products
        .filter((product) => {
          // Type check for productTypeFilter
          if (
            productTypeFilter &&
            parseInt(productTypeFilter) !== product.product_types_id
          ) {
            return false;
          }

          // Status check
          if (statusFilter !== "all" && product.status !== statusFilter) {
            return false;
          }

          // Search term check
          if (searchTerm && searchTerm.trim() !== "") {
            const search = searchTerm.toLowerCase().trim();

            // Check across multiple fields that might contain the search term
            const matchesSearch =
              // Check registration number
              (product.regnumber &&
                product.regnumber.toLowerCase().includes(search)) ||
              // Check base ID
              (product.baseid &&
                product.baseid.toLowerCase().includes(search)) ||
              // Check probe ID - convert to string first in case it's a number
              (product.probeid &&
                String(product.probeid).toLowerCase().includes(search)) ||
              // Check IMEEI
              (product.imeei && product.imeei.toLowerCase().includes(search)) ||
              // Check SIM
              (product.sim && product.sim.toLowerCase().includes(search)) ||
              // Check comments
              (product.comments &&
                product.comments.toLowerCase().includes(search));

            return matchesSearch;
          }

          return true;
        })
        .sort((a, b) => a._id - b._id);

      // Only update the filtered view, not the source data
      setFilteredProducts(newFilteredProducts);
    } catch (error) {
      console.error("Error applying filters:", error);
      setFilteredProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Update your handleAddProduct function
  const handleAddProduct = () => {
    setShowAddModal(true);
  };

  // Function to handle saving new product
  const handleSaveProduct = async (productData) => {
    try {
      // Call your API to save the product
      // If it's a base product, associate it with a base
      if (productData.baseid && productData.probeid === null) {
        const response = await associateBaseWithProduct(productData);
        // Check if the response indicates success
        if (response.code === 201) {
          // Optionally, you can show a success message here
          toast.success(t("product_saved_successfully"));
          // Close the modal
          setShowAddModal(false);
        } else {
          // Handle error case if needed
          toast.error(t("error_associating_base_product"));
        }
      } else {
        const response = await addNewProduct(productData);
        // Check if the response indicates success
        if (response.code === 201) {
          // Optionally, you can show a success message here
          toast.success(t("product_saved_successfully"));
          // Close the modal
          setShowAddModal(false);
        } else {
          // Handle error case if needed
          toast.error(t("error_saving_product"));
        }
      }

      // Refresh products list
      fetchProducts();
      return {
        success: true,
      };
    } catch (error) {
      console.error("Error saving product:", error);
      // Get standardized error details
      const errorData = error.response?.data;
      const errorCode = errorData?.errorCode || "UNKNOWN_ERROR";

      // Show toast with translated error message
      toast.error(t(errorCode, { value: errorData?.value }));

      // Return structured error for form display
      return {
        success: false,
        errorCode,
        error: errorData?.message || t("error_saving_product"),
        errorField: errorData?.field,
        errorValue: errorData?.value,
      };
    }
  };

  // Function to handle saving edited product
  const handleUpdateProduct = async (productData) => {
    try {
      const response = await updateProduct(selectedProduct._id, productData);
      
      if (response.status === 200) {
        toast.success(t("product_updated_successfully"));
        setShowEditModal(false);
        // Clear selected product after update
        setSelectedProduct(null);
        // Refresh products list
        fetchProducts();
      } 
    } catch (error) {
      let errorMessage = "";
      // Handle specific error cases
      if (error.message === "DUPLICATED_REGNUMBER") {
        errorMessage = t("DUPLICATED_REGNUMBER");
      } else if (error.message === "DUPLICATED_PROBEID") {
        errorMessage = t("DUPLICATED_PROBEID");
      } else if (error.message === "DUPLICATED_BASEID") {
        errorMessage = t("DUPLICATED_BASEID");
      } else {
        // Show error toast instead of throwing
        errorMessage = t("error_saving_product");
      }

      toast.error(errorMessage);
    }
  };

  // DELETE PRODUCT
  // Function to handle delete confirmation
  const handleDeleteProduct = async (productId) => {
    try {
      await deleteProduct(productId);
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error(error.response?.data?.message || t("error_deleting_product"));
      // Optionally, you can return structured error info
      return {
        success: false,
        error: error.response?.data?.message || t("error_deleting_product"),
      };
    }
  };

  const handleConfirmDelete = async () => {
    if (productToDelete) {
      const response = await handleDeleteProduct(productToDelete._id);
      // Close the confirmation modal
      setShowDeleteConfirmation(false);
      setProductToDelete(null);

      // If response indicates fail
      if (response && response.success === false) {
        // Handle error case if needed
        console.error("Error deleting product:", response.error);
      }

      // If deletion was successful, you can refresh the products list
      toast.success(t("product_deleted_successfully"));
      // Refresh products list
      fetchProducts();
    }
  };

  // Function to handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setProductToDelete(null);
  };

  // ----- TABLE RELATED FUNCTIONS -----
  // Handle action clicks
  const handleActionClick = (actionType, product) => {
    switch (actionType) {
      case "edit":
        setSelectedProduct(product);
        setShowEditModal(true);
        break;
      case "delete":
        // Show delete confirmation
        // Set the product to delete and show confirmation modal
        setProductToDelete(product);
        setShowDeleteConfirmation(true);
        break;
      default:
        break;
    }
  };

  // Effects
  useEffect(() => {
    fetchProductTypes();
  }, []);

  // Reset to page 1 whenever recordsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [recordsPerPage]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCompany, currentPage, recordsPerPage]);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, productTypeFilter, statusFilter, products]);

  // Calculate pagination
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  return {
    // State
    companies,
    searchTerm,
    setSearchTerm,
    selectedCompany,
    setSelectedCompany,
    // Pagination states
    recordsPerPage,
    setRecordsPerPage,
    currentPage,
    setCurrentPage,
    paginatedProducts,
    // Products states
    loadingProducts,
    selectedProduct,
    setSelectedProduct,
    productToDelete,
    setProductToDelete,
    // Modals
    showAddModal,
    setShowAddModal,
    showEditModal,
    setShowEditModal,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    // Filters
    productTypeFilter,
    setProductTypeFilter,
    statusFilter,
    setStatusFilter,
    products,
    filteredProducts,
    productTypes,

    // Functions
    handleAddProduct,
    handleSaveProduct,
    handleUpdateProduct,
    handleDeleteProduct,
    handleConfirmDelete,
    handleCancelDelete,
    handleActionClick,
    applyFilters,
  };
};
